// Client create/edit form. Used by the Clients page and, in a modal, by the
// invoice editor so a new client can be added without losing the draft.

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Checkbox, ErrorAlert, TextAreaField, TextField } from './ui';
import type { Client } from '@shared/types';

export interface ClientFormProps {
  client?: Client;
  onSaved: (client: Client, created: boolean) => void;
  onCancel: () => void;
}

export function ClientForm({ client, onSaved, onCancel }: ClientFormProps) {
  const t = useT();
  const toMessage = useErrorMessage();

  const [name, setName] = useState(client?.name ?? '');
  const [taxId, setTaxId] = useState(client?.taxId ?? '');
  const [phone, setPhone] = useState(client?.phone ?? '');
  const [email, setEmail] = useState(client?.email ?? '');
  const [address, setAddress] = useState(client?.address ?? '');
  const [isActive, setIsActive] = useState(client?.isActive ?? true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    const payload = { name: name.trim(), taxId, phone, email, address, isActive };
    try {
      const saved = client
        ? await api.updateClient(client.id, payload)
        : await api.createClient(payload);
      onSaved(saved, !client);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      // Only show the banner when nothing could be attached to a field.
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form id="client-form" onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}

      <div className="form-grid form-grid--2">
        <TextField label={t('clients.field.name')} required autoFocus
          wrapperClassName="span-all"
          value={name} error={errors.name} onChange={(e) => setName(e.target.value)} />
        <TextField label={t('clients.field.taxId')} optional
          value={taxId} error={errors.taxId} onChange={(e) => setTaxId(e.target.value)} />
        <TextField label={t('clients.field.phone')} optional type="tel"
          value={phone} error={errors.phone} onChange={(e) => setPhone(e.target.value)} />
        <TextField label={t('clients.field.email')} optional type="email"
          wrapperClassName="span-all"
          value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} />
        <TextAreaField label={t('clients.field.address')} optional rows={3}
          wrapperClassName="span-all"
          value={address} error={errors.address} onChange={(e) => setAddress(e.target.value)} />
      </div>

      <Checkbox label={t('common.active')} checked={isActive} onChange={setIsActive} />

      <div className="row row--end">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy && <span className="spinner" />}
          {busy ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
